export interface RecipeStep {
  title: string;
  instruction: string;
}

export interface Recipe {
  id: number;
  slug: string;
  name: string;
  flag: string;
  cuisine: string;
  description: string;
  prepTime: string;
  cookTime: string;
  totalTime: string;
  servings: number;
  difficulty: string;
  ingredients: string[];
  steps: RecipeStep[];
  servingSuggestions?: string[];
  cookingTips?: string[];
  keywords: string[];
}

export const recipes: Recipe[] = [
  {
    id: 1,
    slug: 'chicken-biryani',
    name: 'Chicken Biryani',
    flag: '🇮🇳',
    cuisine: 'Indian / South Asian',
    description: 'A simple chicken biryani with fragrant rice, tender chicken and warm spices. Easier than it looks and ready in under an hour.',
    prepTime: '20 min',
    cookTime: '35 min',
    totalTime: '55 min',
    servings: 4,
    difficulty: 'Easy',
    ingredients: [
      '500g chicken thighs or breast, cut into pieces',
      '2 cups basmati rice',
      '1 large onion, sliced',
      '2 tomatoes, chopped',
      '2 tablespoons cooking oil',
      '1 cup plain yogurt',
      '3 cloves garlic, minced',
      '1 tablespoon ginger, grated',
      '1 teaspoon turmeric powder',
      '1 teaspoon cumin powder',
      '1 teaspoon coriander powder',
      '1 teaspoon garam masala',
      '1 teaspoon salt',
      '1/2 teaspoon chilli powder (optional)',
      '4 cups water',
      'Fresh coriander leaves for garnish',
    ],
    steps: [
      {
        title: 'Prepare the chicken',
        instruction: 'Add the chicken pieces to a bowl. Mix in the yogurt, half the garlic, half the ginger, turmeric, cumin, coriander, chilli powder and half the salt. Stir well so the chicken is coated. Leave it aside while you prepare the other ingredients.',
      },
      {
        title: 'Wash and soak the rice',
        instruction: 'Place the basmati rice in a sieve and rinse under cold water until the water runs clear. Then soak the rice in a bowl of cold water and set it aside.',
      },
      {
        title: 'Cook the onions',
        instruction: 'Heat 2 tablespoons of oil in a large pan over medium heat. Add the sliced onions and cook for 5 to 6 minutes, stirring often, until they turn golden brown. The onions should look dark and crispy at the edges.',
      },
      {
        title: 'Add the tomatoes and spices',
        instruction: 'Add the chopped tomatoes, the rest of the garlic and ginger, and the garam masala to the pan. Stir and cook for 3 to 4 minutes until the tomatoes soften and the mixture looks like a thick paste.',
      },
      {
        title: 'Cook the chicken',
        instruction: 'Add the marinated chicken (with all the yogurt marinade) to the pan. Stir well. Cook for 8 to 10 minutes, stirring occasionally, until the chicken is no longer pink on the outside. The sauce should thicken and coat the chicken.',
      },
      {
        title: 'Add water and rice',
        instruction: 'Drain the soaked rice. Pour 4 cups of water into the pan and add the rice. Stir gently, then taste the liquid and add more salt if needed. Bring everything to a boil over high heat.',
      },
      {
        title: 'Simmer and steam',
        instruction: 'Once boiling, turn the heat down to the lowest setting. Cover the pan with a tight lid and let it simmer for 15 minutes. Do not open the lid or stir during this time. The rice is done when all the water is absorbed and the grains are tender.',
      },
      {
        title: 'Rest and serve',
        instruction: 'Turn off the heat and leave the pan covered for 5 minutes. Then remove the lid and gently fluff the rice with a fork. Sprinkle fresh coriander leaves on top and serve hot.',
      },
    ],
    servingSuggestions: [
      'Serve with a dollop of plain yogurt or raita on the side.',
      'A simple salad of sliced onions and cucumber pairs well.',
    ],
    cookingTips: [
      'Do not stir the rice once you have covered the pan — this keeps the grains long and separate.',
      'If you do not have all the spices, a shop-bought biryani spice mix works fine.',
    ],
    keywords: ['chicken biryani', 'indian recipe', 'rice', 'biryani', 'home cooking'],
  },
  {
    id: 2,
    slug: 'jerk-chicken-rice-peas',
    name: 'Jerk Chicken with Rice & Peas',
    flag: '🇯🇲',
    cuisine: 'Jamaican / Caribbean',
    description: 'Spicy, smoky jerk chicken served with creamy coconut rice and peas. A Caribbean classic you can make at home in under an hour.',
    prepTime: '15 min',
    cookTime: '40 min',
    totalTime: '55 min',
    servings: 4,
    difficulty: 'Easy',
    ingredients: [
      '4 chicken thighs (bone-in, skin on)',
      '1 tablespoon allspice (or mixed spice)',
      '1 teaspoon dried thyme',
      '1 teaspoon black pepper',
      '1 teaspoon salt',
      '1/2 teaspoon cinnamon',
      '2 spring onions (scallions), chopped',
      '3 cloves garlic, minced',
      '1 scotch bonnet chilli, finely chopped (use less if you want it milder)',
      '1 tablespoon soy sauce',
      '1 tablespoon cooking oil',
      '1 tablespoon lime juice',
      '1 cup long-grain rice',
      '1 can (400g) kidney beans or pigeon peas, drained',
      '1 can (400ml) coconut milk',
      '1 cup water',
      '1 teaspoon salt',
    ],
    steps: [
      {
        title: 'Make the jerk marinade',
        instruction: 'In a small bowl, mix the allspice, thyme, black pepper, salt, cinnamon, spring onions, garlic, scotch bonnet chilli, soy sauce, cooking oil and lime juice. Stir until it forms a paste. It should smell fragrant and spicy.',
      },
      {
        title: 'Marinate the chicken',
        instruction: 'Place the chicken thighs in a bowl or a roasting dish. Rub the jerk paste all over the chicken, making sure to get some under the skin. Leave it to marinate while you prepare the rice.',
      },
      {
        title: 'Start the rice and peas',
        instruction: 'In a medium pot, combine the rice, drained kidney beans, coconut milk, water and salt. Stir once. Place over high heat and bring to a boil.',
      },
      {
        title: 'Simmer the rice',
        instruction: 'Once boiling, turn the heat down to low. Cover the pot with a tight lid and simmer for 18 to 20 minutes. Do not lift the lid. The rice is ready when the liquid is absorbed and the grains are soft.',
      },
      {
        title: 'Cook the chicken',
        instruction: 'While the rice simmers, heat your oven to 200°C (400°F). Place the marinated chicken on a baking tray and roast for 25 to 30 minutes. The chicken is done when the skin is dark and crispy and the juices run clear when you cut into the thickest part.',
      },
      {
        title: 'Rest and serve',
        instruction: 'Take the chicken out of the oven and let it rest for 5 minutes. Fluff the rice with a fork. Serve the jerk chicken on top of or beside the rice and peas.',
      },
    ],
    servingSuggestions: [
      'Serve with fried plantain if you have any.',
      'A simple green salad balances the spicy chicken.',
    ],
    cookingTips: [
      'No scotch bonnet? Use 1/2 teaspoon of chilli flakes instead.',
      'If you have time, marinate the chicken for 2 to 3 hours for a deeper flavour.',
    ],
    keywords: ['jerk chicken', 'jamaican recipe', 'rice and peas', 'caribbean', 'coconut rice'],
  },
  {
    id: 3,
    slug: 'creamy-chicken-pasta',
    name: 'Creamy Chicken Pasta',
    flag: '🇮🇹',
    cuisine: 'Italian-inspired',
    description: 'A rich and creamy chicken pasta with garlic, parmesan and a touch of black pepper. Comfort food that comes together quickly.',
    prepTime: '10 min',
    cookTime: '25 min',
    totalTime: '35 min',
    servings: 4,
    difficulty: 'Easy',
    ingredients: [
      '400g pasta (penne, fusilli or spaghetti)',
      '2 chicken breasts, cut into strips',
      '2 tablespoons cooking oil',
      '3 cloves garlic, minced',
      '200ml double cream (or whipping cream)',
      '1/2 cup grated parmesan cheese',
      '1 teaspoon black pepper',
      '1 teaspoon salt',
      '1 teaspoon dried Italian herbs (or mixed oregano and basil)',
      '1 tablespoon butter',
      'Fresh parsley, chopped for garnish',
    ],
    steps: [
      {
        title: 'Boil the pasta',
        instruction: 'Fill a large pot with water and bring it to a boil. Add a generous pinch of salt, then add the pasta. Cook according to the packet instructions (usually 10 to 12 minutes) until the pasta is soft but still has a slight bite. Stir now and then so it does not stick.',
      },
      {
        title: 'Cook the chicken',
        instruction: 'While the pasta cooks, heat 1 tablespoon of oil in a large pan over medium-high heat. Season the chicken strips with salt and pepper. Add them to the pan and cook for 5 to 6 minutes on each side until golden and cooked through. The chicken should feel firm and have no pink inside. Remove from the pan and set aside.',
      },
      {
        title: 'Make the sauce',
        instruction: 'In the same pan, turn the heat down to medium. Add the remaining tablespoon of oil and the butter. Add the minced garlic and cook for 1 minute until it smells good but is not brown. Pour in the double cream and bring it to a gentle simmer.',
      },
      {
        title: 'Add cheese and seasoning',
        instruction: 'Stir in the parmesan cheese, black pepper, salt and Italian herbs. Keep stirring for 1 to 2 minutes until the cheese melts and the sauce thickens slightly. It should look smooth and creamy.',
      },
      {
        title: 'Combine everything',
        instruction: 'Drain the pasta, saving a small cup of the pasta water. Add the drained pasta and the cooked chicken to the sauce. Toss everything together over low heat for 1 to 2 minutes. If the sauce feels too thick, add a splash of the pasta water to loosen it.',
      },
      {
        title: 'Serve',
        instruction: 'Turn off the heat. Sprinkle fresh parsley and a little extra parmesan on top. Serve immediately while hot and creamy.',
      },
    ],
    servingSuggestions: [
      'Serve with garlic bread or a side salad.',
      'A squeeze of lemon over the pasta brightens the flavour.',
    ],
    cookingTips: [
      'Do not let the garlic brown or it will taste bitter.',
      'Grate your own parmesan if you can — it melts much better than pre-grated.',
    ],
    keywords: ['creamy chicken pasta', 'italian recipe', 'pasta', 'parmesan', 'easy dinner'],
  },
  {
    id: 4,
    slug: 'chicken-chow-mein',
    name: 'Chicken Chow Mein',
    flag: '🇨🇳',
    cuisine: 'Chinese-inspired',
    description: 'A quick stir-fried noodle dish with chicken, cabbage and a savoury sauce. Tastes like a takeaway but ready in 30 minutes.',
    prepTime: '10 min',
    cookTime: '20 min',
    totalTime: '30 min',
    servings: 4,
    difficulty: 'Easy',
    ingredients: [
      '250g egg noodles (or medium egg noodles)',
      '2 chicken breasts, thinly sliced',
      '1 tablespoon cooking oil',
      '1/2 head of cabbage, thinly sliced',
      '1 large carrot, cut into thin strips',
      '1 red pepper, thinly sliced',
      '3 spring onions (scallions), chopped',
      '3 tablespoons soy sauce',
      '1 tablespoon oyster sauce',
      '1 teaspoon sesame oil',
      '1 teaspoon sugar',
      '1/2 teaspoon black pepper',
      '1 clove garlic, minced',
    ],
    steps: [
      {
        title: 'Cook the noodles',
        instruction: 'Bring a pot of water to a boil. Add the noodles and cook according to the packet instructions (usually 3 to 4 minutes for fresh, or 4 to 5 minutes for dried). Drain and rinse under cold water so they do not stick together. Set aside.',
      },
      {
        title: 'Make the sauce',
        instruction: 'In a small bowl, mix the soy sauce, oyster sauce, sesame oil, sugar, black pepper and garlic. Stir until the sugar dissolves. The sauce should look dark and glossy.',
      },
      {
        title: 'Cook the chicken',
        instruction: 'Heat the oil in a large pan or wok over high heat. Add the sliced chicken and stir-fry for 4 to 5 minutes until golden and cooked through. The chicken should have no pink inside. Remove from the pan and set aside.',
      },
      {
        title: 'Stir-fry the vegetables',
        instruction: 'In the same pan, add the cabbage, carrot and red pepper. Stir-fry over high heat for 3 to 4 minutes. The vegetables should soften slightly but still have some crunch. Keep moving them so they do not burn.',
      },
      {
        title: 'Add noodles and sauce',
        instruction: 'Add the cooked noodles and the chicken back to the pan. Pour the sauce over the top. Use tongs or two forks to toss everything together for 2 minutes over high heat. The noodles should be evenly coated and slightly golden.',
      },
      {
        title: 'Serve',
        instruction: 'Turn off the heat. Sprinkle the chopped spring onions on top. Serve hot in bowls.',
      },
    ],
    servingSuggestions: [
      'Serve with extra soy sauce on the side for those who like it saltier.',
      'A drizzle of chilli oil adds a nice kick.',
    ],
    cookingTips: [
      'Prepare all your ingredients before you start cooking — stir-frying is fast.',
      'Keep the heat high so the noodles get a slight char without going soggy.',
    ],
    keywords: ['chicken chow mein', 'chinese recipe', 'noodles', 'stir fry', 'easy dinner'],
  },
  {
    id: 5,
    slug: 'cottage-pie',
    name: 'Cottage Pie',
    flag: '🇬🇧',
    cuisine: 'British',
    description: 'A hearty British cottage pie with a rich beef filling and creamy mashed potato topping. Proper comfort food in under an hour.',
    prepTime: '15 min',
    cookTime: '40 min',
    totalTime: '55 min',
    servings: 4,
    difficulty: 'Easy',
    ingredients: [
      '500g minced beef',
      '1 large onion, chopped',
      '2 carrots, diced',
      '2 cloves garlic, minced',
      '2 tablespoons cooking oil',
      '2 tablespoons plain flour',
      '1 tablespoon tomato puree',
      '300ml beef stock (1 stock cube in 300ml hot water)',
      '1 tablespoon Worcestershire sauce',
      '1 teaspoon dried thyme or rosemary',
      '1 teaspoon salt',
      '1/2 teaspoon black pepper',
      '1 tablespoon frozen peas',
      '4 large potatoes, peeled and chopped',
      '50g butter',
      '50ml milk',
      'A pinch of salt for the potatoes',
    ],
    steps: [
      {
        title: 'Cook the potatoes',
        instruction: 'Place the chopped potatoes in a pot and cover with cold water. Add a pinch of salt. Bring to a boil and cook for 15 to 20 minutes until the potatoes are soft when you poke them with a fork. They should fall apart easily.',
      },
      {
        title: 'Make the mash',
        instruction: 'Drain the potatoes well. Return them to the pot and add the butter and milk. Mash with a fork or masher until smooth. Add a pinch of salt to taste. Set aside.',
      },
      {
        title: 'Cook the beef and onion',
        instruction: 'Heat 1 tablespoon of oil in a large pan over medium heat. Add the chopped onion and diced carrots. Cook for 5 minutes until the onion is soft. Add the garlic and cook for 1 more minute.',
      },
      {
        title: 'Brown the mince',
        instruction: 'Add the minced beef to the pan. Turn the heat up slightly and cook for 5 to 6 minutes, breaking it up with a spoon, until it is brown all over with no pink bits. Drain off any excess fat.',
      },
      {
        title: 'Make the gravy',
        instruction: 'Sprinkle the flour and tomato puree over the beef. Stir well for 1 minute. Pour in the beef stock and Worcestershire sauce. Add the thyme, salt and pepper. Stir and bring to a gentle boil. Turn the heat down and let it simmer for 10 minutes until the sauce thickens. Stir in the frozen peas.',
      },
      {
        title: 'Assemble the pie',
        instruction: 'Heat your oven to 200°C (400°F). Spoon the beef mixture into an ovenproof dish and spread it out evenly. Spoon the mashed potato on top and spread it with a fork to cover the beef completely. Make little peaks in the potato with the fork — these will go crispy.',
      },
      {
        title: 'Bake and serve',
        instruction: 'Place the dish in the oven and bake for 20 minutes until the top is golden brown and the filling is bubbling at the edges. Let it rest for 5 minutes before serving.',
      },
    ],
    servingSuggestions: [
      'Serve with green beans or steamed peas on the side.',
      'A dollop of ketchup on the side is perfectly acceptable.',
    ],
    cookingTips: [
      'Do not skip draining the fat from the mince or the pie will be greasy.',
      'Make the mash while the beef simmers to save time.',
    ],
    keywords: ['cottage pie', 'british recipe', 'minced beef', 'mashed potato', 'comfort food'],
  },
];

export function getRecipeBySlug(slug: string): Recipe | undefined {
  return recipes.find((r) => r.slug === slug);
}
